import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, Timestamp, query, where } from "firebase/firestore";
import { db } from "../../firebase/firebase";

type Organization = {
  id: string;
  name?: string;
  createdAt?: Timestamp | { seconds?: number; toDate?: () => Date } | null;
  admin?: string;
  status?: string;
  compOffEnabled?: boolean;
};

type AdminInfo = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

type OrganizationWithAdmin = Organization & {
  adminName?: string;
  adminId?: string;
  status: string;
  compOffEnabled: boolean;
};

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<OrganizationWithAdmin[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingOrg, setEditingOrg] = useState<OrganizationWithAdmin | null>(null);
  const [newStatus, setNewStatus] = useState<string>("");
  const [newCompOffEnabled, setNewCompOffEnabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [allOrganizations, setAllOrganizations] = useState<OrganizationWithAdmin[]>([]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, "organization"));
        const orgsData: Organization[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as any),
        }));

        // Fetch admin info for each organization
        const orgsWithAdmin: OrganizationWithAdmin[] = await Promise.all(
          orgsData.map(async (org) => {
            let adminName = "—";
            let adminId = org.admin || "—";
            let status = org.status || "";

            // Try to get status from subscriptions collection first
            if (!status) {
              try {
                const subscriptionsRef = collection(db, "subscriptions");
                const q = query(subscriptionsRef, where("orgId", "==", org.id));
                const subscriptionSnapshot = await getDocs(q);
                
                if (!subscriptionSnapshot.empty) {
                  const subscriptionData = subscriptionSnapshot.docs[0].data();
                  status = subscriptionData.status || "";
                }
              } catch (err) {
                console.error(`Error fetching subscription for org ${org.id}:`, err);
              }
            }

            // If no status, calculate based on creation date
            if (!status) {
              const now = new Date();
              const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              let createdDate: Date | null = null;

              if (org.createdAt) {
                if (typeof (org.createdAt as any).toDate === "function") {
                  createdDate = (org.createdAt as any).toDate();
                } else if ((org.createdAt as any).seconds) {
                  createdDate = new Date(((org.createdAt as any).seconds as number) * 1000);
                }
              }

              if (createdDate) {
                if (createdDate >= thirtyDaysAgo) {
                  status = "Trial";
                } else {
                  status = "Active";
                }
              } else {
                status = "Active";
              }
            }

            // Fetch admin details
            if (org.admin) {
              try {
                const adminDoc = await getDoc(doc(db, "admins", org.admin));
                if (adminDoc.exists()) {
                  const adminData = adminDoc.data() as AdminInfo;
                  adminName = `${adminData.firstName || ""} ${adminData.lastName || ""}`.trim() || adminData.email || "—";
                }
              } catch (err) {
                console.error(`Error fetching admin ${org.admin}:`, err);
              }
            }

            return {
              ...org,
              adminName,
              adminId,
              status,
              compOffEnabled: org.compOffEnabled !== false,
            };
          })
        );

        setAllOrganizations(orgsWithAdmin);
        setOrganizations(orgsWithAdmin);
      } catch (err: any) {
        setError(err?.message || "Failed to load organizations");
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  // Filter organizations based on status
  useEffect(() => {
    if (statusFilter === "All") {
      setOrganizations(allOrganizations);
    } else {
      setOrganizations(allOrganizations.filter(org => org.status === statusFilter));
    }
  }, [statusFilter, allOrganizations]);

  const formatDate = (ts: Organization["createdAt"]) => {
    if (!ts) return "-";
    if (typeof (ts as any).toDate === "function") {
      return (ts as any).toDate().toLocaleString();
    }
    if ((ts as any).seconds) {
      return new Date(((ts as any).seconds as number) * 1000).toLocaleString();
    }
    return "-";
  };

  const handleManage = (org: OrganizationWithAdmin) => {
    setEditingOrg(org);
    setNewStatus(org.status);
    setNewCompOffEnabled(org.compOffEnabled);
  };

  const handleSaveStatus = async () => {
    if (!editingOrg || !newStatus) return;

    try {
      setSaving(true);
      
      // Check if subscription exists for this org
      const subscriptionsRef = collection(db, "subscriptions");
      const q = query(subscriptionsRef, where("orgId", "==", editingOrg.id));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // Create new subscription document
        const newSubscriptionRef = doc(collection(db, "subscriptions"));
        await setDoc(newSubscriptionRef, {
          orgId: editingOrg.id,
          status: newStatus,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      } else {
        // Update existing subscription
        const subscriptionDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, "subscriptions", subscriptionDoc.id), {
          status: newStatus,
          updatedAt: Timestamp.now(),
        });
      }

      // Also update organization document for backward compatibility
      const orgRef = doc(db, "organization", editingOrg.id);
      await updateDoc(orgRef, {
        status: newStatus,
        compOffEnabled: newCompOffEnabled,
      });

      // Update local state
      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === editingOrg.id ? { ...org, status: newStatus, compOffEnabled: newCompOffEnabled } : org
        )
      );

      setEditingOrg(null);
      setNewStatus("");
      setNewCompOffEnabled(true);
    } catch (err: any) {
      setError(err?.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "text-[#10B981] bg-[#10B981]/10";
      case "Inactive":
        return "text-[#6B7280] bg-[#6B7280]/10";
      case "Trial":
        return "text-[#FBD282] bg-[#FBD282]/10";
      default:
        return "text-[#C3C9D3] bg-[#2E2F33]";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-[#2E2F33] border-t-[#B59143] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-950/30 border border-red-900 text-red-200">{error}</div>;
  }

  return (
    <>
      {/* Status Filter */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm text-[#C3C9D3] font-medium">Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#1A1C1F] border border-[#2E2F33] text-white rounded-lg focus:border-[#B59143] focus:outline-none text-sm min-w-[150px]"
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Trial">Trial</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-[#C3C9D3]">
            Showing <span className="text-white font-semibold">{organizations.length}</span> of <span className="text-white font-semibold">{allOrganizations.length}</span> organizations
          </div>
          <button
            onClick={() => {
              // Use window.location for reliable navigation outside nested routes
              // This ensures the route is properly matched at the root level
              if (localStorage.getItem("isSuperAdmin") === "true") {
                window.location.href = "/register";
              } else {
                window.location.href = "/login";
              }
            }}
            className="px-4 py-2 bg-[#B59143] hover:bg-[#A7843D] text-white rounded-lg transition-colors duration-200 font-medium text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Register
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-[#2E2F33] bg-[#0F1014]">
        <table className="min-w-full">
          <thead className="bg-[#0F1014]">
            <tr className="text-left text-[#C3C9D3] text-sm">
              <th className="px-4 py-3 border-b border-[#2E2F33]">Name</th>
              <th className="px-4 py-3 border-b border-[#2E2F33]">Organization ID</th>
              <th className="px-4 py-3 border-b border-[#2E2F33]">Admin</th>
              <th className="px-4 py-3 border-b border-[#2E2F33]">Admin ID</th>
              <th className="px-4 py-3 border-b border-[#2E2F33]">Status</th>
              <th className="px-4 py-3 border-b border-[#2E2F33]">Created At</th>
              <th className="px-4 py-3 border-b border-[#2E2F33]">Action</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org.id} className="hover:bg-white/5 transition">
                <td className="px-4 py-3 border-b border-[#1E2024] font-medium text-white text-sm">{org.name || "—"}</td>
                <td className="px-4 py-3 border-b border-[#1E2024] text-[#C3C9D3] font-mono text-xs">{org.id}</td>
                <td className="px-4 py-3 border-b border-[#1E2024] text-[#C3C9D3] text-sm">{org.adminName || "—"}</td>
                <td className="px-4 py-3 border-b border-[#1E2024] text-[#C3C9D3] font-mono text-xs">{org.adminId || "—"}</td>
                <td className="px-4 py-3 border-b border-[#1E2024]">
                  <span className={`inline-block px-2 py-1 text-xs font-medium ${getStatusColor(org.status)}`}>
                    {org.status}
                  </span>
                </td>
                <td className="px-4 py-3 border-b border-[#1E2024] text-[#C3C9D3] text-xs">{formatDate(org.createdAt)}</td>
                <td className="px-4 py-3 border-b border-[#1E2024]">
                  <button
                    onClick={() => handleManage(org)}
                    className="px-3 py-1 text-xs bg-[#1A1C1F] border border-[#2E2F33] text-[#C3C9D3] hover:border-[#B59143] hover:text-white transition"
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td className="px-4 py-10 text-center text-[#C3C9D3]" colSpan={7}>No organizations found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Manage Status Modal */}
      {editingOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0F1014] border border-[#2E2F33] p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[#E8C16C] mb-4">Manage Organization Status</h3>
            <p className="text-sm text-[#C3C9D3] mb-2">Organization: <span className="text-white font-medium">{editingOrg.name}</span></p>
            
            <div className="mb-4">
              <label className="block text-sm text-[#C3C9D3] mb-2">Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full px-3 py-2 bg-[#1A1C1F] border border-[#2E2F33] text-white focus:border-[#B59143] focus:outline-none"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Trial">Trial</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-[#C3C9D3] mb-2">Comp Off</label>
              <select
                value={newCompOffEnabled ? "On" : "Off"}
                onChange={(e) => setNewCompOffEnabled(e.target.value === "On")}
                className="w-full px-3 py-2 bg-[#1A1C1F] border border-[#2E2F33] text-white focus:border-[#B59143] focus:outline-none"
              >
                <option value="On">On</option>
                <option value="Off">Off</option>
              </select>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setEditingOrg(null);
                  setNewStatus("");
                  setNewCompOffEnabled(true);
                }}
                className="px-4 py-2 bg-[#1A1C1F] border border-[#2E2F33] text-[#C3C9D3] hover:border-[#2E2F33] transition"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStatus}
                disabled={saving || !newStatus}
                className="px-4 py-2 bg-[#B59143] text-white hover:bg-[#A7843D] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Organizations;


